import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from '@/hooks/useDebounce.ts'
import { apiClient } from '@/lib/api.ts'
import { useModuleStatus } from '@/hooks/useModuleStatus.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import {
  Badge,
  BetaBadge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
} from '@/components/ui'
import {
  Shield,
  Settings,
  ChevronRight,
  Building2,
  CheckCircle2,
  WifiOff,
  Search,
} from 'lucide-react'

interface CustomerWithStructure {
  id: string
  name: string
  customerNumber?: string
  street?: string
  zip?: string
  city?: string
  structure: {
    latestVersion: number
    lastUpdated: string
  } | null
}

export function ItStructuresOverviewPage() {
  const a = useAnonymize()
  const { isBeta } = useModuleStatus('it-strukturen')
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)

  const { data, isLoading, error, refetch } = useQuery<{
    customers: CustomerWithStructure[]
    tanssError?: string | null
    searchActive?: boolean
  }>({
    queryKey: ['it-structures-customers', debouncedSearch],
    queryFn: () => {
      const params = debouncedSearch.length >= 2 ? `?q=${encodeURIComponent(debouncedSearch)}` : ''
      return apiClient.get(`/it-structures/customers${params}`).then((r) => r.data)
    },
    retry: 1,
  })

  const customers = data?.customers ?? []

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Shield className="w-7 h-7 text-brand-500" />
            IT-Strukturen & Compliance
            {isBeta ? <BetaBadge size="md" /> : null}
          </span>
        }
        description="AD-Strukturen definieren, Zugriffsmatrizen verwalten und Deployments dokumentieren"
        actions={
          <Button
            variant="secondary"
            leadingIcon={<Settings className="w-4 h-4 text-brand-500" />}
            onClick={() => navigate('/infrastruktur/strukturen/global')}
          >
            Globale Standards
          </Button>
        }
      />

      <Input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Kunden in TANSS suchen (min. 2 Zeichen)…"
        leadingIcon={<Search />}
      />

      {error && (
        <ErrorState
          size="sm"
          title="Fehler beim Laden"
          description={error instanceof Error ? error.message : String(error)}
          onRetry={() => refetch()}
        />
      )}

      {!error && data?.tanssError && (
        <Card className="border-amber-200 bg-amber-50">
          <div className="p-3 text-small text-amber-800 flex items-start gap-2">
            <WifiOff className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <span className="font-medium">TANSS nicht erreichbar</span> – Kundenliste kann nicht
              geladen werden.
              <details className="mt-1">
                <summary className="cursor-pointer text-caption text-amber-700 hover:underline">
                  Details anzeigen
                </summary>
                <pre className="mt-1 text-caption font-mono bg-amber-100 rounded p-2 whitespace-pre-wrap break-all">
                  {data.tanssError}
                </pre>
              </details>
            </div>
          </div>
        </Card>
      )}

      <Card>
        {isLoading && <LoadingState label="Lade Kunden…" />}
        {!isLoading && customers.length === 0 && (
          <EmptyState
            icon={<Building2 />}
            title={search.length < 2 ? 'Kundennamen eingeben' : 'Keine Kunden gefunden'}
            description={
              search.length < 2
                ? 'Kundennamen eingeben um in TANSS zu suchen. Bestehende Strukturen erscheinen automatisch.'
                : data?.tanssError
                  ? 'TANSS nicht verfügbar.'
                  : 'Es wurden keine passenden Kunden gefunden.'
            }
          />
        )}
        {!isLoading && customers.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {customers.map((customer) => (
              <li key={customer.id}>
                <button
                  onClick={() =>
                    navigate(`/infrastruktur/strukturen/${customer.id}`, {
                      state: { customerName: customer.name },
                    })
                  }
                  className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-gray-50 transition-colors group text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Building2 className="w-5 h-5 text-gray-400 group-hover:text-brand-500 transition-colors flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-body font-medium text-gray-900 truncate">
                        {a.name(customer.name)}
                      </p>
                      <p className="text-caption text-gray-400 mt-0.5 flex flex-wrap gap-x-3">
                        {customer.customerNumber && (
                          <span>KdNr. {a.code(customer.customerNumber)}</span>
                        )}
                        <span className="text-gray-300">ID {a.code(customer.id)}</span>
                        {!a.enabled && (customer.street || customer.city) && (
                          <span>
                            {[
                              customer.street,
                              customer.zip && customer.city
                                ? `${customer.zip} ${customer.city}`
                                : customer.city,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        )}
                        {customer.structure ? (
                          <span className="inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Version {customer.structure.latestVersion} · zuletzt{' '}
                            {new Date(customer.structure.lastUpdated).toLocaleDateString('de-DE')}
                          </span>
                        ) : (
                          <Badge variant="neutral" size="sm">
                            Keine Struktur
                          </Badge>
                        )}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-brand-500 transition-colors flex-shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="border-blue-200 bg-blue-50">
        <div className="p-4">
          <h3 className="text-body font-semibold text-blue-900 flex items-center gap-2 mb-1">
            <Shield className="w-4 h-4" />
            Wie funktioniert das?
          </h3>
          <ol className="text-caption text-blue-700 space-y-1 list-decimal list-inside">
            <li>
              Kunden auswählen und AD-Struktur definieren (OUs, Server, Gruppen, Zugriffsmatrix)
            </li>
            <li>Verschlüsselten Payload exportieren (AES-256)</li>
            <li>Payload in das PowerShell-Script auf dem Kundenserver einspielen</li>
            <li>Script führt die Konfiguration durch und sendet das Ergebnis zurück</li>
          </ol>
        </div>
      </Card>
    </div>
  )
}
